import type {
  GoogleImportRequest,
  TakeoutLocationRef,
  TakeoutReviewPayload,
} from "../googleReviews";
import type { TakeoutReadResult } from "./readTakeoutExport";

/**
 * Předvybraná lokace (F2.5): zapamatovaná z posledního potvrzení, když je v exportu;
 * jinak jediná lokace; jinak `null` a admin vynutí výběr.
 */
export function defaultLocationKey(
  locations: readonly TakeoutLocationRef[],
  remembered: string | null,
): string | null {
  if (remembered !== null && locations.some((l) => l.locationKey === remembered)) return remembered;
  return locations.length === 1 ? locations[0].locationKey : null;
}

/** Recenze složená výčtem polí: jméno, text a čas nese právě kvalifikovaná (pět hvězd s textem). */
function payload(review: TakeoutReviewPayload): TakeoutReviewPayload {
  const { id, stars, hasText } = review;
  if (stars === 5 && hasText) {
    return {
      id,
      stars,
      hasText,
      author: review.author,
      text: review.text,
      createTime: review.createTime,
    };
  }
  return { id, stars, hasText };
}

/**
 * Tělo požadavku náhledu i potvrzení importu pro jednu lokaci. Recenze ostatních lokací
 * se do něj nedostanou; počet recenzí se neořezává (strop hlídá API). Neexistující klíč
 * je chyba programu (admin vybírá jen z výsledku čtení) → `RangeError`.
 */
export function toImportRequest(
  result: Extract<TakeoutReadResult, { ok: true }>,
  locationKey: string,
): GoogleImportRequest {
  const location = result.locations.find((l) => l.locationKey === locationKey);
  if (!location) throw new RangeError(`Lokace ${locationKey} v exportu není`);
  return {
    location: { locationKey: location.locationKey, name: location.name, placeId: location.placeId },
    exportDate: result.exportDate,
    exportDateSource: result.exportDateSource,
    reviews: location.reviews.map(payload),
  };
}
